import api, { IPaginationParams, IPaginationResponse } from 'config/axios';
import fileDownload from 'js-file-download';
import AppService from 'common/app-service';
import { IContractSimulation } from 'common/contract-simulation.interface';
import {
  IContractTakerRoot,
  ContractTypes,
  IContract,
  IContractLimitsConcentration,
  IContractProposalDetails,
  IContractSecondaryOperation,
  IContractTotals,
  ICreateLimitsConcentrationSettings,
  ILimitsConcentrationSettings,
} from 'common/contract.interface';
import { PAGINATION_PAGE_LIMIT } from 'utils/api';
import { IContractFiltersSecondaryOp } from 'common/secondary-operation.interface';
import { ICTe } from 'common/cte.interface';

interface ProposalParams {
  ctesIds: number[];
  dueDays: number;
}

export interface IContractFilters {
  dueDateStart?: string;
  dueDateEnd?: string;
  status?: ContractTypes | ContractTypes[];
  intentionDateStart?: string;
  intentionDateEnd?: string;
  sortOrder?: string;
  sortBy?: string;
}

export interface IContractPaymentData {
  paymentValue: number;
  penaltyAmount: number;
  interestChargeAmount: number;
  baseValue: number;
  paymentDiscountAmount: number;
}

export default class ContractsService extends AppService {
  constructor() {
    super('contracts', api);
  }

  async calculateContractSimulation({ ctesIds, dueDays }: ProposalParams): Promise<IContractSimulation> {
    const response = await this.api.post(this.root('simulation'), { ctesIds, dueDays });
    return response.data;
  }

  async create({ ctesIds, dueDays }: ProposalParams) {
    const response = await this.api.post(this.root(), { ctesIds, dueDays });
    return response.data;
  }

  async getAll(
    { dueDateStart, dueDateEnd, status, intentionDateEnd, intentionDateStart, sortBy, sortOrder }: IContractFilters = {},
    { page, limit }: Partial<IPaginationParams> = {}
  ): Promise<IPaginationResponse<IContract>> {
    const response = await this.api.get(this.root(), {
      params: {
        dueDateStart,
        dueDateEnd,
        intentionDateEnd,
        intentionDateStart,
        page: page || 1,
        limit: limit || PAGINATION_PAGE_LIMIT,
        status: status && status.length > 0 ? status : undefined,
        sortOrder,
        sortBy,
      },
    });
    return response.data;
  }

  async getContractsProposalStatus(contractIds: number[]): Promise<IContractProposalDetails[]> {
    const response = await this.api.post(this.root('contracts-proposal-status'), {
      contractIds,
    });

    return response.data;
  }

  async patch(id: number | string, params: Partial<IContract>): Promise<void> {
    const response = await this.api.patch(this.root(id), params);
    return response.data;
  }

  async getById(contractId: number): Promise<any> {
    const response = await this.api.get(this.root(contractId));
    return response.data;
  }

  async getTakers(contractId: number): Promise<IContractTakerRoot[]> {
    const response = await this.api.get(this.root(`${contractId}/takers`));
    return response.data;
  }

  async getTotalAnticipatedAmount({ dueDateStart, dueDateEnd, status }: IContractFilters): Promise<IContractTotals> {
    const response = await this.api.get(this.root('total-anticipated-amount'), {
      params: {
        dueDateStart,
        dueDateEnd,
        status: status && status.length > 0 ? status : undefined,
      },
    });
    return response.data;
  }

  async setApproval({ contractId, isApproved, reason }: { contractId: number; isApproved: boolean; reason: string }): Promise<IContract> {
    const response = await this.api.put(this.root(`${contractId}/approval`), {
      isApproved,
      reason,
    });
    return response.data;
  }

  async getCount({ status }: { status: ContractTypes }): Promise<{ count: number }> {
    const response = await this.api.get(this.root('count'), {
      params: {
        status,
      },
    });
    return response.data.count;
  }

  async getTotals(): Promise<any> {
    const response = await this.api.get(this.root('totals'));
    return response.data;
  }

  async calculateContractPayment({ contractId }: { contractId: number }): Promise<IContractPaymentData> {
    const response = await this.api.get(this.root(`${contractId}/interest`));

    return response.data;
  }

  async getContractSecondaryOperation({
    secondaryOperationType,
    contractCreatedAtMin,
    contractCreatedAtMax,
    contractDueDateMin,
    contractDueDateMax,
    carrierRootId,
    contractId,
  }: IContractFiltersSecondaryOp): Promise<IContractSecondaryOperation[]> {
    const params = {
      secondaryOperationType,
      ...(contractId && { contractId }),
      ...(carrierRootId && { carrierRootId }),
      ...(contractCreatedAtMin && { contractCreatedAtMin: contractCreatedAtMin.toFormat('yyyy-MM-dd') }),
      ...(contractCreatedAtMax && { contractCreatedAtMax: contractCreatedAtMax.toFormat('yyyy-MM-dd') }),
      ...(contractDueDateMin && { contractDueDateMin: contractDueDateMin.toFormat('yyyy-MM-dd') }),
      ...(contractDueDateMax && { contractDueDateMax: contractDueDateMax.toFormat('yyyy-MM-dd') }),
    };
    const response = await this.api.get(this.root('available/secondary-operation'), { params });
    return response.data;
  }

  async uploadSettlementCSV({ contractId, file }: { file: File; contractId: number }): Promise<IContract> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(this.root(`${contractId}/settle-ctes`), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      transformRequest: () => {
        // known issue to perform a formData post https://github.com/axios/axios/issues/4406
        return formData;
      },
    });

    return response.data;
  }

  async getDownloadLink(id: number): Promise<{ url: string; filename: string }> {
    const response = await this.api.get(this.root(`${id}/download.pdf`));
    return response.data;
  }

  async getContractLimitsAndConcentration(contractId: number): Promise<IContractLimitsConcentration[]> {
    const response = await this.api.get(this.root(`${contractId}/limits-concentration`));
    return response.data;
  }

  async getCurrentLimitsAndConcentrationSettings(): Promise<ILimitsConcentrationSettings> {
    const response = await this.api.get(this.root('limits-concentration/current'));
    return response.data;
  }

  async createLimitsConcentrationSettings(payload: ICreateLimitsConcentrationSettings): Promise<ILimitsConcentrationSettings> {
    const response = await this.api.post(this.root('limits-concentration'), { ...payload });

    return response.data;
  }

  async getContractCtes(contractId: number, params?: { limit?: number; offset?: number }): Promise<IPaginationResponse<ICTe>> {
    const limit = params?.limit;
    const offset = params?.offset || 0;
    const response = await this.api.get(this.root(`${contractId}/ctes`), {
      params: { limit, offset: (offset || 0) * PAGINATION_PAGE_LIMIT },
    });

    return response.data;
  }

  async getCtesXlsx(contractId: number) {
    const response = await this.api.get(this.root(`${contractId}/ctes/xlsx`), {
      headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      data: {},
      responseType: 'blob',
    });

    fileDownload(response.data, `ctes_${contractId}.xlsx`);
  }

  async getBtgXlsx(contractId: number) {
    const response = await this.api.get(this.root(`${contractId}/ctes/btg`), {
      headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      data: {},
      responseType: 'blob',
    });

    fileDownload(response.data, `ctes_btg_${contractId}.xlsx`);
  }

  async getCtesXml(contractId: number) {
    const response = await this.api.get(this.root(`${contractId}/ctes/xml`), {
      responseType: 'blob',
    });

    fileDownload(response.data, `ctes_${contractId}.zip`);
  }

  async getContractTerm() {
    const response = await this.api.get(this.root('terms'));

    return response.data;
  }

  async notifyTaker(contractId: number, carrierRootId: number, takerRootId: number, dryRun?: boolean): Promise<void | string> {
    const response = await this.api.post(this.root(`${contractId}/notify-taker`), { carrierRootId, takerRootId, dryRun });

    return response.data;
  }
}
